import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Icon } from "./Icon";
import { kbCategoryMeta } from "../config/kbCategories";
import { api, type KbArticleListItem } from "../services/api";

/** Self-contained bookmark toggle. Seeds from is_saved; flips optimistically. */
export function BookmarkButton({ id, initialSaved }: { id: string; initialSaved: boolean }) {
  const [saved, setSaved] = useState(initialSaved);
  const [busy, setBusy] = useState(false);

  async function toggle(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    const next = !saved;
    setSaved(next);
    setBusy(true);
    try {
      if (next) await api.kb.save(id);
      else await api.kb.unsave(id);
    } catch {
      setSaved(!next); // revert on failure
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className={"kb-bm" + (saved ? " on" : "")}
      onClick={toggle}
      title={saved ? "Saved" : "Save"}
      aria-pressed={saved}
    >
      <Icon name="bookmark" size={17} fill={saved} />
    </button>
  );
}

/** A single article row (list view). */
export function ArtRow({ article }: { article: KbArticleListItem }) {
  const cat = kbCategoryMeta(article.category);
  const isGuide = article.article_type === "guide";
  return (
    <Link to={`/kb/article/${article.id}`} className="kb-art-row">
      <span className={`kb-art-rowico tint-${cat.tint}`}>
        <Icon name={isGuide ? "list" : "doc"} size={18} />
      </span>
      <span className="kb-art-rowbody">
        <span className="kb-art-rowtitle">
          {article.title}
          {isGuide && (
            <span className="kb-tag-guide">
              <Icon name="play" size={8} fill />
              Guide
            </span>
          )}
        </span>
        {article.summary && <span className="kb-art-rowsum">{article.summary}</span>}
      </span>
      <BookmarkButton id={article.id} initialSaved={article.is_saved} />
      <Icon name="chevRight" size={18} className="kb-art-arrow" />
    </Link>
  );
}

/** A guided-walkthrough spotlight card. */
export function GuideCard({ article }: { article: KbArticleListItem }) {
  const cat = kbCategoryMeta(article.category);
  return (
    <Link to={`/kb/article/${article.id}`} className={`kb-guide-card tint-${cat.tint}`}>
      <div className="kb-guide-top">
        <span className={`kb-guide-ico tint-${cat.tint}`}>
          <Icon name={cat.icon} size={20} />
        </span>
        <span className="kb-guide-tag">
          <Icon name="play" size={9} fill />
          Step-by-step
        </span>
      </div>
      <div className="kb-guide-title">{article.title}</div>
      {article.summary && <div className="kb-guide-desc">{article.summary}</div>}
      <div className="kb-guide-meta">
        <span>{article.category}</span>
      </div>
    </Link>
  );
}

/** A category card linking to its listing. */
export function CatCard({ category, count }: { category: string; count?: number }) {
  const meta = kbCategoryMeta(category);
  return (
    <Link to={`/kb/category/${encodeURIComponent(category)}`} className="kb-cat-card">
      <span className={`kb-cat-ico tint-${meta.tint}`}>
        <Icon name={meta.icon} size={22} />
      </span>
      <span className="kb-cat-body">
        <span className="kb-cat-name">{category}</span>
        {meta.blurb && <span className="kb-cat-blurb">{meta.blurb}</span>}
      </span>
      {count !== undefined && (
        <span className="kb-cat-count">
          {count} {count === 1 ? "article" : "articles"}
        </span>
      )}
      <Icon name="chevRight" size={18} className="kb-cat-arrow" />
    </Link>
  );
}

/** Hero / inline search box that navigates to the search results route. */
export function KbSearchBox({ initial = "", big = true }: { initial?: string; big?: boolean }) {
  const [q, setQ] = useState(initial);
  const navigate = useNavigate();
  function submit() {
    const trimmed = q.trim();
    if (trimmed) navigate(`/kb/search?q=${encodeURIComponent(trimmed)}`);
  }
  return (
    <div className={"kb-search-box" + (big ? "" : " sm")}>
      <Icon name="search" size={20} className="kb-search-ico" />
      <input
        value={q}
        placeholder="Search how-tos, fixes and guides — or ask a question…"
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
        aria-label="Search the knowledge base"
      />
      <button type="button" className="kb-search-btn" onClick={submit}>
        <Icon name="search" size={15} />
        Search
      </button>
    </div>
  );
}
